import { useState, useCallback, useMemo } from 'react';
import type { CatalogItem } from '@peles/shared';
import { useCreateExpenseItem } from '../../hooks/useExpenses';
import { useExpenseConfig } from '../../hooks/useExpenses';
import { useVendorCategories } from '../../hooks/useVendors';
import { useCreateVendor } from '../../hooks/useVendors';
import { VendorAutosuggest } from './VendorAutosuggest';

interface CreateExpensePanelProps {
  eventId: string;
  onClose: () => void;
  onCreated: () => void;
}

interface FormState {
  vendorCategoryId: string;
  catalogItemId: string;
  itemName: string;
  vendorName: string;
  vendorId: string;
  price: string;
  statusId: string;
  dueDate: string;
  comments: string;
}

const initialForm: FormState = {
  vendorCategoryId: '',
  catalogItemId: '',
  itemName: '',
  vendorName: '',
  vendorId: '',
  price: '',
  statusId: '',
  dueDate: '',
  comments: '',
};

export function CreateExpensePanel({ eventId, onClose, onCreated }: CreateExpensePanelProps) {
  const createExpenseItem = useCreateExpenseItem();
  const createVendor = useCreateVendor();
  const { data: config } = useExpenseConfig();
  const [form, setForm] = useState<FormState>(initialForm);
  const [error, setError] = useState('');

  // Group catalog items by category
  const catalogItemsByCategory = useMemo(() => {
    if (!config) return new Map<string, CatalogItem[]>();
    const map = new Map<string, CatalogItem[]>();
    for (const item of config.catalogItems) {
      const existing = map.get(item.vendorCategoryId) || [];
      existing.push(item);
      map.set(item.vendorCategoryId, existing);
    }
    return map;
  }, [config]);

  const filteredCatalogItems = form.vendorCategoryId
    ? catalogItemsByCategory.get(form.vendorCategoryId) || []
    : [];

  // Price is optional — an expense line can be created without an amount and
  // priced later; when typed it must be a non-negative number.
  const isValid =
    form.vendorCategoryId !== '' && (form.price === '' || parseFloat(form.price) >= 0);

  const handleCreate = useCallback(() => {
    setError('');
    createExpenseItem.mutate(
      {
        eventId,
        vendorCategoryId: form.vendorCategoryId,
        catalogItemId: form.catalogItemId || undefined,
        itemName: form.itemName.trim() || undefined,
        vendorId: form.vendorId || undefined,
        vendorName: form.vendorName.trim() || undefined,
        price: form.price === '' ? undefined : parseFloat(form.price),
        statusId: form.statusId || undefined,
        dueDate: form.dueDate || undefined,
        comments: form.comments.trim() || undefined,
      },
      {
        onSuccess: () => {
          onCreated();
        },
        onError: (err: unknown) => {
          const message =
            (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
            'Failed to create expense item';
          setError(message);
        },
      },
    );
  }, [form, eventId, createExpenseItem, onCreated]);

  const handleCreateVendor = useCallback(
    (name: string) => {
      createVendor.mutate(
        { businessName: name },
        {
          onSuccess: (vendor) => {
            setForm((f) => ({ ...f, vendorName: vendor.businessName, vendorId: vendor.id }));
          },
          onError: () => {
            setError('Failed to create vendor');
          },
        },
      );
    },
    [createVendor],
  );

  const { data: vendorCategories = [] } = useVendorCategories();

  return (
    <div className="md:border-l border-gray-200 p-4 md:p-6 bg-white overflow-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold">Add Expense</h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          aria-label="Close panel"
        >
          &times;
        </button>
      </div>

      {error && (
        <div className="mb-4 px-3 py-2 bg-red-50 text-red-700 text-sm rounded">{error}</div>
      )}

      <div className="space-y-4">
        <div>
          <label
            htmlFor="create-expense-category"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Category *
          </label>
          <select
            id="create-expense-category"
            value={form.vendorCategoryId}
            onChange={(e) =>
              setForm((f) => ({ ...f, vendorCategoryId: e.target.value, catalogItemId: '' }))
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Select category...</option>
            {vendorCategories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>

        {filteredCatalogItems.length > 0 && (
          <div>
            <label
              htmlFor="create-expense-catalog-item"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Catalog Item
            </label>
            <select
              id="create-expense-catalog-item"
              value={form.catalogItemId}
              onChange={(e) => setForm((f) => ({ ...f, catalogItemId: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Select item...</option>
              {filteredCatalogItems.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label
            htmlFor="create-expense-item-name"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Item Name
          </label>
          <input
            id="create-expense-item-name"
            type="text"
            value={form.itemName}
            onChange={(e) => setForm((f) => ({ ...f, itemName: e.target.value }))}
            placeholder="Optional custom item name"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Name</label>
          <VendorAutosuggest
            value={form.vendorName}
            vendorId={form.vendorId || null}
            onChange={(vendorName, vendorId) =>
              setForm((f) => ({ ...f, vendorName, vendorId: vendorId || '' }))
            }
            onCreateVendor={handleCreateVendor}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="create-expense-price"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Price
            </label>
            <input
              id="create-expense-price"
              type="number"
              value={form.price}
              onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
              placeholder="0.00"
              min="0"
              step="0.01"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label
              htmlFor="create-expense-status"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Status
            </label>
            <select
              id="create-expense-status"
              value={form.statusId}
              onChange={(e) => setForm((f) => ({ ...f, statusId: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Default</option>
              {config?.statuses.map((status) => (
                <option key={status.id} value={status.id}>
                  {status.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label
            htmlFor="create-expense-due-date"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Due Date
          </label>
          <input
            id="create-expense-due-date"
            type="date"
            value={form.dueDate}
            onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label
            htmlFor="create-expense-comments"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Comments
          </label>
          <textarea
            id="create-expense-comments"
            value={form.comments}
            onChange={(e) => setForm((f) => ({ ...f, comments: e.target.value }))}
            placeholder="Optional"
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
          />
        </div>
      </div>

      <div className="mt-6 flex gap-3">
        <button
          onClick={handleCreate}
          disabled={!isValid || createExpenseItem.isPending}
          className="px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {createExpenseItem.isPending ? 'Creating...' : 'Add Expense'}
        </button>
        <button
          onClick={onClose}
          className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
